import { cloneDeep, filter, find } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useApolloClient, useQuery } from "@apollo/client/react/hooks";
import { settingGreen } from "../../../../common/colors";
import { DefaultCurrencyScenarios, PROJECT_COST_SUMMARY_HEADERS } from "./constants";
import {
  AppState,
  CostCurrencyScenario,
  CostHeaderMeasures,
  CurrencyScenario,
  EstimateCodeSelectionState,
  EstimateType,
  HierarchyItemEnum,
  ProjectCostColumnId,
  ProjectCostItem,
  ProjectCostsByCurrencyCode,
  ProjectCostsEditing,
  ProjectCostsEstimateCodesResult,
  ProjectEstimateCodeCostItem,
} from "../../../../common/types";
import CostItemDetails from "./CostItemDetails";
import CostsDetails from "./CostsDetails";
import CurrencyScenarioSelection from "./CurrencyScenarioSelection";
import { GET_PROJECT_COSTS_ESTIMATE_CODES, queryProjectCostsItem } from "./queries";
import { clearItemActuals, updateChildren } from "./utils";
import LoadingView from "../../../LoadingView";
import { connect } from "react-redux";
import FullscreenSpinner from "../../../FullscreenSpinner";
import ProjectCostEstimationStatus from "./ProjectCostEstimationStatus";
import { ApolloClient } from "@apollo/client";

interface ProjectCostsProps {
  projectId: number;
  userCanEdit: boolean;
  hasCostsAccessOnly: boolean;
}

const mapStateToProps = (state: AppState) => ({
  description: state.projectHeaderState.item && state.projectHeaderState.item.description,
  projectVirtualType:
    state.projectHeaderState.item && state.projectHeaderState.item.type === HierarchyItemEnum.Project
      ? state.projectHeaderState.item.projectVirtualType
      : undefined,
});

const updateCurrencyScenarios = (costItemsByCurrencyCodes: ProjectCostsByCurrencyCode[]): CurrencyScenario[] => {
  return costItemsByCurrencyCodes.map(item => {
    return {
      id: item.currencyScenario,
      desc: item.currencyCode,
      estimatesCurrencyId: item.currencyScenario === CostCurrencyScenario.Custom ? item.currencyId : 1,
      actualsCurrencyId: item.currencyId,
      currencyCode: item.currencyCode,
      costItemsByEstimateCode: item.costItemsByEstimateCode,
      costHeaderMeasures: item.costHeaderMeasures,
      editable: item.currencyScenario === CostCurrencyScenario.LcProject,
    };
  });
};

const updateChildColumns = (items: ProjectCostItem[], column: ProjectCostColumnId, value: number | null) => {
  items.forEach(item => {
    item.values[column] = value;
    if (item.childItems) updateChildColumns(item.childItems, column, value);
  });
};

// TODO: Implement without mutating the existing data. Instead, return a new list of items.
//       Current implementation works only, if a deep clone of the original "filteredProjectItems"
//       is passed, as the values are mutated in-place.
const updateProjectCostItemColumn = (
  estimateSelectionState: EstimateCodeSelectionState,
  estimatesCostItemsByEstimateCode: ProjectEstimateCodeCostItem[] | undefined,
  filteredProjectItems: ProjectCostItem[],
  column: ProjectCostColumnId,
  baseItems: ProjectEstimateCodeCostItem | undefined,
  findDefaultPrevEstCode: (currentEstCode: string) => string | undefined
) => {
  if (column === "estimateChange") {
    if (estimatesCostItemsByEstimateCode) {
      const prevEstCodeSelection = estimateSelectionState["prevEstimate"];
      const curEstCodeSelection = estimateSelectionState["currentEstimate"];
      const prevEstCodeCostItem =
        prevEstCodeSelection !== undefined
          ? find(estimatesCostItemsByEstimateCode, item => item.estimateCode === prevEstCodeSelection.id)
          : undefined;
      const curEstCodeCostItem =
        curEstCodeSelection !== undefined
          ? find(estimatesCostItemsByEstimateCode, item => item.estimateCode === curEstCodeSelection.id)
          : undefined;
      if (prevEstCodeCostItem && curEstCodeCostItem) {
        filteredProjectItems.forEach(item => {
          const prevFoundItem = find(prevEstCodeCostItem.costItems, prevItem => prevItem.id === item.id);
          const curFoundItem = find(curEstCodeCostItem.costItems, curItem => curItem.id === item.id);
          if (prevFoundItem && curFoundItem) {
            item.values[column] =
              curFoundItem.values["currentEstimate"] !== undefined &&
              curFoundItem.values["currentEstimate"] !== null &&
              prevFoundItem.values["currentEstimate"] !== undefined &&
              prevFoundItem.values["currentEstimate"] !== null
                ? curFoundItem.values["currentEstimate"] - prevFoundItem.values["currentEstimate"]
                : null;
            if (item.childItems && curFoundItem.childItems && prevFoundItem.childItems) {
              updateChildren(item.childItems, curFoundItem.childItems, column, prevFoundItem.childItems);
            }
          }
        });
      }
    }
  } else if (column === "ETC") {
    const curEstCodeSelection = estimateSelectionState.currentEstimate;
    if (estimatesCostItemsByEstimateCode) {
      const curEstCodeCostItem =
        curEstCodeSelection !== undefined
          ? find(estimatesCostItemsByEstimateCode, item => item.estimateCode === curEstCodeSelection.id)
          : undefined;
      if (baseItems && curEstCodeCostItem) {
        filteredProjectItems.forEach(item => {
          const baseFoundItem = find(baseItems.costItems, baseItem => baseItem.id === item.id);
          const curFoundItem = find(curEstCodeCostItem.costItems, curItem => curItem.id === item.id);
          if (baseFoundItem && curFoundItem) {
            const currentEstimate = curFoundItem.values["currentEstimate"] || 0;
            const committed = baseFoundItem.values["committed"] || 0;
            item.values[column] = currentEstimate - committed;
            if (item.childItems && curFoundItem.childItems && baseFoundItem.childItems) {
              updateChildren(item.childItems, curFoundItem.childItems, column, baseFoundItem.childItems);
            }
          }
        });
      }
    }
  } else if (column === "currentEstimate") {
    const curEstCodeSelection = estimateSelectionState["currentEstimate"];
    const defaultPrevEstCode = curEstCodeSelection ? findDefaultPrevEstCode(curEstCodeSelection.id) : undefined;
    filteredProjectItems.forEach(item => {
      if (estimatesCostItemsByEstimateCode) {
        const curEstCodeCostItem = find(
          estimatesCostItemsByEstimateCode,
          item => item.estimateCode === (curEstCodeSelection ? curEstCodeSelection.id : undefined)
        );
        const prevEstCodeCostItem = find(
          estimatesCostItemsByEstimateCode,
          item => item.estimateCode === defaultPrevEstCode
        );

        const curFoundItem = curEstCodeCostItem
          ? find(curEstCodeCostItem.costItems, curItem => curItem.id === item.id)
          : undefined;
        const prevFoundItem = prevEstCodeCostItem
          ? find(prevEstCodeCostItem.costItems, prevItem => prevItem.id === item.id)
          : undefined;

        if (curFoundItem) item.values.currentEstimate = curFoundItem.values.currentEstimate;

        if (item.childItems && curFoundItem && curFoundItem.childItems)
          updateChildren(item.childItems, curFoundItem.childItems, column, prevFoundItem && prevFoundItem.childItems);
      }
    });
  } else if (column == "periodicWipCosts") {
    const curEstCodeSelection = estimateSelectionState.currentEstimate;
    const prevEstCodeSelection = estimateSelectionState.prevEstimate;
    if (estimatesCostItemsByEstimateCode) {
      const curEstCodeCostItem =
        curEstCodeSelection !== undefined
          ? find(estimatesCostItemsByEstimateCode, item => item.estimateCode === curEstCodeSelection.id)
          : undefined;
      const prevEstCodeCostItem =
        prevEstCodeSelection !== undefined
          ? find(estimatesCostItemsByEstimateCode, item => item.estimateCode === prevEstCodeSelection.id)
          : undefined;
      if (prevEstCodeCostItem && curEstCodeCostItem) {
        filteredProjectItems.forEach(item => {
          const baseFoundItem = find(prevEstCodeCostItem.costItems, baseItem => baseItem.id === item.id);
          const curFoundItem = find(curEstCodeCostItem.costItems, curItem => curItem.id === item.id);
          if (baseFoundItem && curFoundItem) {
            const currentCumulativeWipCosts = curFoundItem.values.wipCostsCumulative || 0;
            const baseCumulativeWipCosts = baseFoundItem.values.wipCostsCumulative || 0;
            item.values.periodicWipCosts = currentCumulativeWipCosts - baseCumulativeWipCosts;
            if (item.childItems && curFoundItem.childItems && baseFoundItem.childItems) {
              updateChildren(item.childItems, curFoundItem.childItems, column, baseFoundItem.childItems);
            }
          }
        });
      }
    }
  } else {
    const estimateCodeSelection = estimateSelectionState[column === "estimatedHours" ? "currentEstimate" : column];
    if (estimatesCostItemsByEstimateCode) {
      const estimateCodeCostItem =
        estimateCodeSelection !== undefined
          ? find(estimatesCostItemsByEstimateCode, item => item.estimateCode === estimateCodeSelection.id)
          : undefined;
      if (estimateCodeCostItem) {
        estimateCodeCostItem.costItems.forEach(item => {
          const foundItem = find(filteredProjectItems, filteredItem => filteredItem.id === item.id);
          if (foundItem) {
            const updateColumn: ProjectCostColumnId = column === "prevEstimate" ? "currentEstimate" : column;
            foundItem.values[column] = item.values[updateColumn];
            if (foundItem.childItems && item.childItems) {
              updateChildren(foundItem.childItems, item.childItems, column);
            }
          }
        });
      } else {
        filteredProjectItems.forEach(project => {
          project.values[column] = null;
          if (project.childItems) updateChildColumns(project.childItems, column, null);
        });
      }
    }
  }
};
const getEstimatesItems = (
  selectedCurrencyScenario: CurrencyScenario,
  currencyScenarios: CurrencyScenario[]
): ProjectEstimateCodeCostItem[] | undefined => {
  if (
    selectedCurrencyScenario.id === CostCurrencyScenario.Custom ||
    selectedCurrencyScenario.actualsCurrencyId === selectedCurrencyScenario.estimatesCurrencyId
  ) {
    return selectedCurrencyScenario.costItemsByEstimateCode;
  } else {
    const findEstimates = find(
      currencyScenarios,
      item =>
        item.id !== CostCurrencyScenario.Custom &&
        item.actualsCurrencyId === selectedCurrencyScenario.estimatesCurrencyId
    );
    return findEstimates?.costItemsByEstimateCode;
  }
};

const filterCostItemsByEstimate = (
  estimateSelectionState: EstimateCodeSelectionState,
  actualsCostItemsByEstimateCode: ProjectEstimateCodeCostItem[] | undefined,
  estimatesCostItemsByEstimateCode: ProjectEstimateCodeCostItem[] | undefined
) => {
  const filteredProjectItems: ProjectCostItem[] = [];
  const clearActuals = !actualsCostItemsByEstimateCode;
  const baseItems =
    (actualsCostItemsByEstimateCode && actualsCostItemsByEstimateCode[0]) ||
    (estimatesCostItemsByEstimateCode && estimatesCostItemsByEstimateCode[0]);
  if (baseItems) {
    baseItems.costItems.forEach(item => {
      const newItem = cloneDeep(item);
      filteredProjectItems.push(newItem);
    });
    const estimateCodesListDesc = estimatesCostItemsByEstimateCode
      ? estimatesCostItemsByEstimateCode.map(item => parseInt(item.estimateCode)).sort((a, b) => b - a)
      : undefined;
    const findDefaultPrevEstCode = (estCode: string): string | undefined => {
      if (estimateCodesListDesc === undefined) return undefined;
      else {
        const currentIndex = estimateCodesListDesc.findIndex(code => code.toString() === estCode);
        return currentIndex > -1 && currentIndex + 1 < estimateCodesListDesc.length
          ? estimateCodesListDesc[currentIndex + 1].toString()
          : undefined;
      }
    };
    filter(PROJECT_COST_SUMMARY_HEADERS, header => header.hasEstimateCode)
      .map(header => header.id)
      .forEach(column => {
        updateProjectCostItemColumn(
          estimateSelectionState,
          estimatesCostItemsByEstimateCode,
          filteredProjectItems,
          column,
          baseItems,
          findDefaultPrevEstCode
        );
      });
    if (clearActuals) clearItemActuals(filteredProjectItems);
  }

  return filteredProjectItems;
};

const findAllLeafItems = (costItem: ProjectCostItem): ProjectCostItem[] => {
  if (costItem.childItems == undefined || costItem.childItems.length == 0) {
    return [costItem];
  } else {
    return costItem.childItems.flatMap(item => findAllLeafItems(item));
  }
};

function ProjectCosts(props: ProjectCostsProps & ReturnType<typeof mapStateToProps>): React.ReactElement {
  const client = useApolloClient() as ApolloClient<Record<string, unknown>>;
  const { projectId, description: projectDescription, projectVirtualType, userCanEdit, hasCostsAccessOnly } = props;
  const [filteredItems, setFilteredItems] = useState<ProjectCostItem[]>([]);
  const [leafItems, setLeafItems] = useState<ProjectCostItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<ProjectCostItem | undefined>(undefined);
  const [detailsPopupOpen, setDetailsPopupOpen] = useState<boolean>(false);
  const [openProjectItems, setOpenProjectItems] = useState<string[]>([]);
  const [cacheUpdated, setCacheUpdated] = useState<number | undefined>(undefined);
  const [estimateSelectionState, setEstimateSelectionState] = useState<EstimateCodeSelectionState>({
    asSold: undefined,
    originalBudget: undefined,
    proposedBudgetChange: undefined,
    revisedBudget: undefined,
    prevEstimate: undefined,
    currentEstimate: undefined,
  });
  const [itemsLoading, setItemsLoading] = useState<boolean>(true);
  const [currencyScenarios, setCurrencyScenarios] = useState(DefaultCurrencyScenarios);
  const [selectedCurrencyScenario, setSelectedCurrencyScenario] = useState(DefaultCurrencyScenarios[0]);
  const [selectedHeaderMeasures, setSelectedHeaderMeasures] = useState<CostHeaderMeasures | undefined>(undefined);
  const [costsEditing, setCostsEditing] = useState<ProjectCostsEditing | undefined>();
  const [estimateCodeIds, setEstimateCodeIds] = useState<string[] | undefined>(undefined);

  const queryVariables = { projectId };

  const mergeCostItems = (
    originSet: ProjectEstimateCodeCostItem[],
    newSet: ProjectEstimateCodeCostItem[]
  ): ProjectEstimateCodeCostItem[] => {
    return newSet.reduce((acc, estimateCode) => {
      const found = acc.length === 0 ? -1 : acc.findIndex(i => estimateCode.estimateCode === i.estimateCode);
      if (found < 0) {
        return [...acc, estimateCode];
      } else {
        let origin = acc[found];
        const mergedEstimateCode = { ...origin, costItems: origin.costItems.concat(estimateCode.costItems) };
        return [...acc.slice(0, found), mergedEstimateCode].concat(acc.slice(found + 1));
      }
    }, originSet);
  };
  const mergeCurrencyScenarios = (originSet: CurrencyScenario[], newSet: CurrencyScenario[]): CurrencyScenario[] => {
    return newSet.reduce<CurrencyScenario[]>((scenarioSet, scenario) => {
      const found =
        scenarioSet.length === 0
          ? -1
          : scenarioSet.findIndex(
              s =>
                s.id === scenario.id &&
                s.currencyCode === scenario.currencyCode &&
                s.actualsCurrencyId === scenario.actualsCurrencyId
            );
      if (found < 0) {
        return [...scenarioSet, scenario];
      } else {
        let origin = scenarioSet[found];
        const mergedScenario = {
          ...origin,
          costItemsByEstimateCode: mergeCostItems(origin.costItemsByEstimateCode, scenario.costItemsByEstimateCode),
        };
        return [...scenarioSet.slice(0, found), mergedScenario].concat(scenarioSet.slice(found + 1));
      }
    }, originSet);
  };

  function groupBySize<T>(items: T[], size: number): T[][] {
    let grouped: T[][] = [];
    for (let start = 0; start < items.length; start += 10) {
      grouped.push(items.slice(start, start + 10));
    }
    return grouped;
  }

  const {
    loading: estimateCodesLoading,
    data: estimateCodsData,
    error: estimateCodesError,
    refetch,
  } = useQuery<ProjectCostsEstimateCodesResult>(GET_PROJECT_COSTS_ESTIMATE_CODES, {
    variables: queryVariables,
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      if (!data) return;

      setEstimateCodeIds(
        Array.from(
          new Set(data.projectCostsEstimateCodes.estimateTypes.flatMap(type => type.estimateCodes.map(c => c.id)))
        )
      );
      setCostsEditing(data.projectCostsEstimateCodes.editing);
      const asSoldEstimateCode = find(
        data.projectCostsEstimateCodes.estimateTypes,
        item => item.estimateType === EstimateType.AsSold
      );
      const originalBudgetEstimateCode = find(
        data.projectCostsEstimateCodes.estimateTypes,
        item => item.estimateType === EstimateType.OriginalBudget
      );
      const proposedBudgetChangeEstimateCode = find(
        data.projectCostsEstimateCodes.estimateTypes,
        item => item.estimateType === EstimateType.ProposedBudgetChange
      );
      const revisedBudgetEstimateCode = find(
        data.projectCostsEstimateCodes.estimateTypes,
        item => item.estimateType === EstimateType.RevisedBudget
      );
      const currentEstimateCode = find(
        data.projectCostsEstimateCodes.estimateTypes,
        item => item.estimateType === EstimateType.Estimate
      );

      const currentEstimate = currentEstimateCode
        ? currentEstimateCode.estimateCodes.length
          ? currentEstimateCode.estimateCodes[0]
          : undefined
        : undefined;

      let prevEstimate;

      if (currentEstimate && currentEstimateCode && currentEstimateCode.estimateCodes.length) {
        const filteredCodes = filter(
          currentEstimateCode.estimateCodes,
          code => code.id.slice(0, code.id.length - 2) !== currentEstimate.id.slice(0, code.id.length - 2)
        );
        if (filteredCodes.length) {
          prevEstimate = filteredCodes[0];
        } else {
          prevEstimate = currentEstimateCode.estimateCodes[0];
        }
      }

      setEstimateSelectionState({
        asSold: asSoldEstimateCode
          ? asSoldEstimateCode.estimateCodes.length
            ? asSoldEstimateCode.estimateCodes[0]
            : undefined
          : undefined,
        originalBudget: originalBudgetEstimateCode
          ? originalBudgetEstimateCode.estimateCodes.length
            ? originalBudgetEstimateCode.estimateCodes[0]
            : undefined
          : undefined,
        proposedBudgetChange: proposedBudgetChangeEstimateCode
          ? proposedBudgetChangeEstimateCode.estimateCodes.length
            ? proposedBudgetChangeEstimateCode.estimateCodes[0]
            : undefined
          : undefined,
        revisedBudget: revisedBudgetEstimateCode
          ? revisedBudgetEstimateCode.estimateCodes.length
            ? revisedBudgetEstimateCode.estimateCodes[0]
            : undefined
          : undefined,
        prevEstimate,
        currentEstimate,
      });
    },
  });

  // Group estimate codes by 10 and query project costs items by estimate codes because some projects are too large to query at once
  useEffect(() => {
    setItemsLoading(true);
    if (estimateCodeIds !== undefined && estimateCodeIds.length > 0) {
      Promise.all(
        groupBySize(estimateCodeIds, 10).map(groupedIds => {
          console.log("groupedIds ", groupedIds);
          return queryProjectCostsItem(client, projectId, groupedIds);
        })
      ).then(responses => {
        const scenarios = responses.reduce<CurrencyScenario[]>((acc, [data, errors]) => {
          console.log("!!!");
          if (!data || errors) return acc;
          else return mergeCurrencyScenarios(acc, updateCurrencyScenarios(data.costItemsByCurrencyCode));
        }, []);
        setCurrencyScenarios(scenarios);
        setItemsLoading(false);
      });
    } else setItemsLoading(false);
  }, [estimateCodeIds, queryProjectCostsItem]);

  const estimateLeafItems = useMemo(() => {
    if (!estimateCodsData || itemsLoading) return [];

    const estimateItemsByCurrency = getEstimatesItems(DefaultCurrencyScenarios[0], currencyScenarios);
    if (!estimateItemsByCurrency) return [];

    const estimate = estimateCodsData.projectCostsEstimateCodes.estimateTypes.find(
      item => item.estimateType == EstimateType.Estimate
    );
    if (!estimate || estimate.estimateCodes.length === 0) return [];

    const latestEstimateCodeId = (function () {
      const sorted = estimate.estimateCodes.map(code => code.id).sort((a, b) => a.localeCompare(b));
      return sorted[sorted.length - 1];
    })();
    const latestEstimate = estimateItemsByCurrency.find(item => item.estimateCode == latestEstimateCodeId);
    return ((latestEstimate && latestEstimate.costItems) || []).flatMap(item => findAllLeafItems(item));
  }, [estimateCodsData, itemsLoading, currencyScenarios]);

  const onEstimateSelectionStateChange = useCallback(() => {
    if (!itemsLoading) {
      const estimateItemsByCurrency = getEstimatesItems(selectedCurrencyScenario, currencyScenarios);
      const filteredItems = filterCostItemsByEstimate(
        estimateSelectionState,
        selectedCurrencyScenario.costItemsByEstimateCode,
        estimateItemsByCurrency
      );
      setFilteredItems(filteredItems);
      setLeafItems(filteredItems.length > 0 ? findAllLeafItems(filteredItems[0]) : []);
      !hasCostsAccessOnly &&
        selectedCurrencyScenario.costItemsByEstimateCode &&
        setSelectedHeaderMeasures(selectedCurrencyScenario.costHeaderMeasures);
    }
  }, [estimateSelectionState, itemsLoading, selectedCurrencyScenario, currencyScenarios, hasCostsAccessOnly]);

  useEffect(() => {
    if (currencyScenarios.length > 0) {
      setSelectedCurrencyScenario(currencyScenarios[0]);
    }
  }, [currencyScenarios]);
  useEffect(onEstimateSelectionStateChange, [estimateSelectionState, selectedCurrencyScenario]);

  useEffect(() => {
    if (cacheUpdated) onEstimateSelectionStateChange();
  }, [cacheUpdated, onEstimateSelectionStateChange]);

  const itemDetails = selectedItem !== undefined && projectVirtualType !== undefined && (
    <CostItemDetails
      title={selectedItem.description}
      projectId={projectId}
      projectVirtualType={projectVirtualType}
      itemId={selectedItem.id}
      selectedCurrencyScenario={selectedCurrencyScenario}
      currencyRate={selectedItem.currencyRate || 1.0}
      popupOpen={detailsPopupOpen}
      closePopup={() => setDetailsPopupOpen(false)}
    />
  );

  // if all leaf activities are blocked, this project is finished and can't be edited anymore.
  const allBlocked =
    estimateLeafItems.length !== 0 && estimateLeafItems.every(item => item.tooltip && item.tooltip.isBlocked);
  // null and 0 is considered as the same value here
  // VALPOPAMS-394: previous estimate values can actually have decimals, but new estimate values are always rounded
  // to the nearest integer value. Thus, we might incorrectly detect a change in the estimate, when comparing to the
  // previous estimate, unless we round the values first
  const currentEstBlockedAndChanged = estimateLeafItems.filter(
    item =>
      item.tooltip &&
      item.tooltip.isBlocked &&
      item.values.lastEstimate !== undefined &&
      Math.round(item.values.currentEstimate || 0) != Math.round(item.values.lastEstimate || 0)
  );

  console.log("Blocked and changed:", currentEstBlockedAndChanged);

  return (
    <Container>
      {estimateCodsData && !estimateCodesError && costsEditing !== undefined && !itemsLoading && (
        <ContentContainer>
          <TabsRow>
            <CurrencyScenarioSelection
              currencyScenarios={currencyScenarios}
              selectedCurrencyScenario={selectedCurrencyScenario}
              currencyScenarioSelected={currencyScenario => setSelectedCurrencyScenario(currencyScenario)}
            />
            <ProjectCostEstimationStatus
              costEstimationStatus={estimateCodsData.projectCostsEstimateCodes.costEstimationStatus}
              editing={costsEditing}
            />
          </TabsRow>
          <ScenarioContainer>
            <CurrencyDetails>
              {selectedCurrencyScenario.id}: {selectedCurrencyScenario.currencyCode}
            </CurrencyDetails>
            {projectVirtualType !== undefined && (
              <CostsDetails
                projectId={projectId}
                projectVirtualType={projectVirtualType}
                projectItems={filteredItems}
                onSelectedForDetails={setSelectedItem}
                selectedDetailsItem={selectedItem}
                projectDescription={projectDescription}
                onSave={() => refetch(queryVariables)}
                estimateCodesAndStatuses={estimateCodsData.projectCostsEstimateCodes.estimateTypes}
                estimateCodesSelectionState={estimateSelectionState}
                setEstimateCodesSelectionState={setEstimateSelectionState}
                refetchData={refetch}
                openProjectItems={openProjectItems}
                setOpenProjectItems={setOpenProjectItems}
                onCacheUpdated={setCacheUpdated}
                userCanEdit={userCanEdit && costsEditing.enabled}
                editing={costsEditing}
                allBlocked={allBlocked}
                currentEstBlockedAndChanged={currentEstBlockedAndChanged}
                selectedCurrencyScenario={selectedCurrencyScenario}
                headerMeasures={selectedHeaderMeasures}
                openDetailsPopup={() => setDetailsPopupOpen(true)}
              />
            )}
            {itemDetails}
          </ScenarioContainer>
        </ContentContainer>
      )}
      {(estimateCodsData === undefined && estimateCodesLoading) || (itemsLoading && <LoadingView />)}
      {estimateCodsData !== undefined && estimateCodesLoading && <FullscreenSpinner text={"Reloading"} />}
      {estimateCodesError && <LoadingContainer>Error loading project estimate codes.</LoadingContainer>}
      {!estimateCodesLoading && !estimateCodesError && !estimateCodsData && (
        <LoadingContainer>No data available.</LoadingContainer>
      )}
    </Container>
  );
}

export default connect(mapStateToProps)(ProjectCosts);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100% - 10px);
  margin: 0 5px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  @media (min-width: 2000px) {
    align-self: center;
    min-width: 1800px;
    max-width: 100%;
  }
  @media (max-width: 2000px) {
    min-width: 1500px;
    max-width: 100%;
  }
  @media (max-width: 1500px) {
    min-width: 1500px;
  }
`;

const TabsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: end;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ScenarioContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${settingGreen};
  padding-top: 20px;
  padding-bottom: 20px;
  width: 100%;
`;

const CurrencyDetails = styled.div`
  padding: 0px 20px;
  font-size: 16px;
`;
